/**
 * `useVerifiedSiteKey` — guard against the STALE shown-once key.
 *
 * The SiteProvider caches the shown-once raw `site_key` per site in
 * localStorage (ADR-0010 §3.2 ergonomics). That cache has no
 * invalidation signal: if the key is rotated from another tab (or any
 * out-of-band path), this tab keeps "remembering" a dead key — and a
 * hard refresh does NOT clear localStorage. Observed live 2026-06-12
 * on the johnnyvbt store: Settings rendered a snippet + agent-card URL
 * for a key two rotations dead.
 *
 * The guard: fetch the single-site read (which carries `site_key_hash`
 * per ADR-0056), hash the cached raw key with WebCrypto, and only
 * return the key when `base64url(sha256(raw)) === site_key_hash`. On
 * mismatch the dead cache entry is purged and `null` is returned — the
 * consuming surface falls back to its KEY-HIDDEN affordance, whose
 * "generate a new key" path is the correct recovery.
 *
 * Trust posture: the hash check is a CONSISTENCY guard, not an authz
 * boundary (both values are already client-visible). Fail-open on
 * environments without WebCrypto/fetch oddities — a false "valid" just
 * reproduces today's behavior; a false "hidden" would block the
 * legitimate post-rotate flow.
 */

import { useEffect, useState } from 'react';

import type { Site } from '@sill/types';

import { useApi } from '../api/api-context.js';

const SITE_KEY_STORAGE_PREFIX = 'sill:site_key:';

async function sha256Base64Url(input: string): Promise<string> {
  const buf = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(input),
  );
  let bin = '';
  for (const b of new Uint8Array(buf)) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function useVerifiedSiteKey(site: Site): string | null {
  const api = useApi();
  const [verified, setVerified] = useState<string | null>(null);

  const raw_key =
    typeof site.site_key === 'string' && site.site_key.length > 0
      ? site.site_key
      : null;

  useEffect(() => {
    let cancelled = false;
    if (raw_key === null) {
      setVerified(null);
      return undefined;
    }
    void (async (): Promise<void> => {
      try {
        // The LIST read omits site_key_hash; the single-site GET
        // carries it (ADR-0056). Prefer the value already on the
        // object when present.
        let expected = site.site_key_hash;
        if (typeof expected !== 'string' || expected.length === 0) {
          const detail = await api.sites.get(site.site_id);
          expected = detail?.site_key_hash;
        }
        if (typeof expected !== 'string' || expected.length === 0) {
          // No hash available — nothing to verify against; keep the
          // pre-guard behavior.
          if (!cancelled) setVerified(raw_key);
          return;
        }
        const actual = await sha256Base64Url(raw_key);
        if (cancelled) return;
        if (actual === expected) {
          setVerified(raw_key);
        } else {
          setVerified(null);
          try {
            // Purge from localStorage — the SiteProvider persists the raw
            // key there (founder 2026-07-29; it's a public embed value, so
            // it survives across sessions rather than dying with the tab).
            localStorage.removeItem(SITE_KEY_STORAGE_PREFIX + site.site_id);
          } catch {
            /* storage disabled — nothing to purge */
          }
        }
      } catch {
        // Fail-open (see header) — verification infrastructure errors
        // must not hide a legitimate key.
        if (!cancelled) setVerified(raw_key);
      }
    })();
    return (): void => {
      cancelled = true;
    };
  }, [api, raw_key, site.site_id, site.site_key_hash]);

  return verified;
}
